import React, { useEffect, useMemo, useState, useRef } from "react";
import { Plus, Trash2, FileText, Paperclip, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDate, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel, StatusBadge } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useVariants, VariantSelect } from "../components/VariantPicker";

export default function PurchaseRequests() {
  const { can } = useAuth();
  const [list, setList] = useState([]);
  const [statusF, setStatusF] = useState("all");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const write = can("warehouse_admin", "purchasing");
  const approver = can("supervisor");

  const load = () => api.get("/purchase-requests", { params: { status: statusF === "all" ? undefined : statusF } }).then((r) => setList(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusF]);

  const act = async (id, action) => {
    try { await api.post(`/purchase-requests/${id}/${action}`); toast.success("Berhasil"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="purchase-requests-page">
      <PageHeader title="Purchase Request" subtitle="Permintaan pengadaan barang">
        {write && <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-pr-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><Plus className="w-4 h-4 mr-1.5" /> Purchase Request</Button>}
      </PageHeader>

      <div className="mb-4">
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="w-52 bg-white" data-testid="pr-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["all", "Draft", "Submitted", "Approved", "Rejected", "Converted to PO", "Cancelled"].map((s) => <SelectItem key={s} value={s}>{s === "all" ? "Semua Status" : s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Panel>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>No. PR</TableHead><TableHead>Tanggal</TableHead><TableHead>Supplier</TableHead>
              <TableHead className="text-center">Item</TableHead><TableHead>Pemohon</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((pr) => (
              <TableRow key={pr.id} data-testid={`pr-row-${pr.id}`}>
                <TableCell className="font-mono text-sm font-semibold text-cocoa">{pr.number}</TableCell>
                <TableCell className="text-sm">{fmtDate(pr.date)}</TableCell>
                <TableCell>{pr.supplier_name || "-"}</TableCell>
                <TableCell className="text-center">{pr.items?.length || 0}</TableCell>
                <TableCell className="text-sm">{pr.requester_name}</TableCell>
                <TableCell><StatusBadge status={pr.status} /></TableCell>
                <TableCell className="text-right space-x-1 whitespace-nowrap">
                  {pr.status === "Draft" && write && <><Button size="sm" variant="outline" onClick={() => { setEdit(pr); setOpen(true); }} className="border-taupe text-cocoa h-7 px-2" data-testid={`pr-edit-${pr.id}`}>Edit</Button><Button size="sm" onClick={() => act(pr.id, "submit")} className="bg-cocoa text-ivory h-7 px-2" data-testid={`pr-submit-${pr.id}`}>Submit</Button></>}
                  {pr.status === "Submitted" && approver && <><Button size="sm" onClick={() => act(pr.id, "approve")} className="bg-status-passed text-white h-7 px-2" data-testid={`pr-approve-${pr.id}`}>Approve</Button><Button size="sm" variant="outline" onClick={() => act(pr.id, "reject")} className="border-status-reject text-status-reject h-7 px-2" data-testid={`pr-reject-${pr.id}`}>Reject</Button></>}
                  {pr.status === "Approved" && write && <Button size="sm" onClick={() => act(pr.id, "convert-to-po")} className="bg-taupe text-white h-7 px-2" data-testid={`pr-convert-${pr.id}`}>Convert ke PO</Button>}
                  {pr.status === "Converted to PO" && <span className="text-xs text-wmuted">→ {pr.converted_po_number}</span>}
                  {["Draft", "Submitted", "Approved"].includes(pr.status) && write && <Button size="sm" variant="ghost" onClick={() => act(pr.id, "cancel")} className="text-status-reject h-7 px-2" data-testid={`pr-cancel-${pr.id}`}>Batal</Button>}
                </TableCell>
              </TableRow>
            ))}
            {!list.length && <TableRow><TableCell colSpan={7} className="text-center text-wmuted py-10"><FileText className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada Purchase Request.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Panel>

      <PRDialog open={open} setOpen={setOpen} edit={edit} onDone={load} />
    </div>
  );
}

function PRDialog({ open, setOpen, edit, onDone }) {
  const { flat } = useVariants();
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({ supplier_id: "", reason: "", needed_date: "", notes: "" });
  const [items, setItems] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [pending, setPending] = useState([]);
  const fileRef = useRef();
  const editable = !edit || ["Draft", "Submitted"].includes(edit.status);

  const loadAtt = (id) => api.get(`/purchase-requests/${id}/attachments`).then((r) => setAttachments(r.data)).catch(() => {});

  useEffect(() => {
    if (!open) return;
    setPending([]); setAttachments([]);
    api.get("/suppliers", { params: { active: "active" } }).then((r) => setSuppliers(r.data));
    if (edit) {
      setForm({ supplier_id: edit.supplier_id || "", reason: edit.reason || "", needed_date: (edit.needed_date || "").slice(0, 10), notes: edit.notes || "" });
      setItems((edit.items || []).map((it) => ({ ...it })));
      loadAtt(edit.id);
    } else { setForm({ supplier_id: "", reason: "", needed_date: "", notes: "" }); setItems([]); }
  }, [open, edit]);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "jpg", "jpeg", "png", "xls", "xlsx"].includes(ext)) return toast.error("Tipe file tidak diizinkan");
    if (f.size > 10 * 1024 * 1024) return toast.error("Ukuran file melebihi 10 MB");
    if (edit) {
      const fd = new FormData(); fd.append("file", f);
      try { await api.post(`/purchase-requests/${edit.id}/attachments`, fd); toast.success("Lampiran diunggah"); loadAtt(edit.id); }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    } else { setPending((p) => [...p, f]); }
  };
  const delAtt = async (aid) => {
    try { await api.delete(`/purchase-requests/${edit.id}/attachments/${aid}`); loadAtt(edit.id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addItem = () => setItems([...items, { variant_id: "", qty: 1, est_hpp: 0 }]);
  const setItem = (i, patch) => { const n = [...items]; n[i] = { ...n[i], ...patch }; setItems(n); };
  const onPick = (i, vid) => {
    const f = flat.find((x) => x.variant_id === vid);
    setItem(i, f ? { variant_id: vid, product_id: f.product_id, product_name: f.product_name, color: f.color, size: f.size, sku: f.sku } : { variant_id: vid });
  };

  const save = async () => {
    const clean = items.filter((it) => it.variant_id && parseInt(it.qty) > 0)
      .map((it) => ({ ...it, qty: parseInt(it.qty) || 0, est_hpp: parseInt(it.est_hpp) || 0 }));
    if (!clean.length) return toast.error("Tambahkan minimal satu item");
    const payload = { ...form, needed_date: form.needed_date || null, items: clean };
    try {
      let prId = edit?.id;
      if (edit) await api.put(`/purchase-requests/${edit.id}`, payload);
      else { const res = await api.post("/purchase-requests", payload); prId = res.data.id; }
      for (const f of pending) { const fd = new FormData(); fd.append("file", f); await api.post(`/purchase-requests/${prId}/attachments`, fd); }
      toast.success("Purchase Request disimpan"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">{edit ? `Edit ${edit.number}` : "Purchase Request Baru"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Supplier / Konveksi</Label>
            <Select value={form.supplier_id || undefined} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
              <SelectTrigger data-testid="pr-supplier-select"><SelectValue placeholder="Pilih supplier" /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Tanggal Dibutuhkan</Label><Input type="date" value={form.needed_date} onChange={(e) => setForm({ ...form, needed_date: e.target.value })} data-testid="pr-needed-date" /></div>
          <div className="col-span-2"><Label>Alasan Pengadaan</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="pr-reason" /></div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <Label>Item</Label>
            <Button size="sm" variant="outline" onClick={addItem} className="border-taupe text-cocoa" data-testid="pr-add-item"><Plus className="w-3.5 h-3.5 mr-1" /> Item</Button>
          </div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-[1fr_90px_130px_auto] gap-2 items-center">
                <VariantSelect flat={flat} value={it.variant_id} onChange={(v) => onPick(i, v)} testId={`pr-item-variant-${i}`} />
                <Input type="number" placeholder="Qty" value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} data-testid={`pr-item-qty-${i}`} />
                <Input type="number" placeholder="Est. HPP" value={it.est_hpp} onChange={(e) => setItem(i, { est_hpp: e.target.value })} data-testid={`pr-item-hpp-${i}`} />
                <Button size="icon" variant="ghost" onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-status-reject" /></Button>
              </div>
            ))}
            {!items.length && <div className="text-sm text-wmuted py-2">Belum ada item.</div>}
          </div>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <Label>Lampiran (PDF/JPG/PNG/XLS · maks 10MB)</Label>
            {editable && <>
              <input type="file" ref={fileRef} onChange={onFile} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx" data-testid="pr-attach-input" />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} className="border-taupe text-cocoa" data-testid="pr-attach-btn"><Paperclip className="w-3.5 h-3.5 mr-1" /> Unggah</Button>
            </>}
          </div>
          <div className="space-y-1" data-testid="pr-attachments">
            {attachments.map((a) => (
              <div key={a.id} className="flex items-center justify-between text-sm border border-taupe/15 rounded px-2 py-1">
                <a href={fileUrl(a.storage_path)} target="_blank" rel="noreferrer" className="text-cocoa hover:underline flex items-center gap-1.5"><Download className="w-3.5 h-3.5" />{a.original_filename}</a>
                {editable && <Button size="icon" variant="ghost" onClick={() => delAtt(a.id)} data-testid={`pr-attach-del-${a.id}`}><Trash2 className="w-4 h-4 text-status-reject" /></Button>}
              </div>
            ))}
            {pending.map((f, i) => (
              <div key={"p" + i} className="flex items-center justify-between text-sm text-wmuted border border-dashed border-taupe/30 rounded px-2 py-1">
                <span className="flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5" />{f.name} (akan diunggah)</span>
                <Button size="icon" variant="ghost" onClick={() => setPending(pending.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-status-reject" /></Button>
              </div>
            ))}
            {!attachments.length && !pending.length && <div className="text-xs text-wmuted">Belum ada lampiran.</div>}
          </div>
        </div>
        <div className="mt-3"><Label>Catatan</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} data-testid="save-pr-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
